import { ChangeDetectionStrategy, Component } from '@angular/core';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { AutoTempModes, CoolingModes, TemperatureControlModes, ThermModes } from '@library/store/homes/homes.interface';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { SidebarControlModeService } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-sidebar/sidebar-control-mode/sidebar-control-mode.service';
import { combineLatest, firstValueFrom, map } from 'rxjs';

@Component({
  selector: 'app-home-mode-selection',
  templateUrl: './home-mode-selection.component.html',
  styleUrls: ['./home-mode-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeModeSelectionComponent {

  private hasNAC$ = this.modulesFacade.currentHomeModules$.pipe(
    map((modules) => modules.some((module) => module.type === 'NAC')),
  );
  private hasBAC$ = this.modulesFacade.currentHomeModules$.pipe(
    map((modules) => modules.some((module) => module.type === 'BAC')),
  );

  private displayOff$ = combineLatest({
    hasNAC: this.hasNAC$,
    hasBAC: this.hasBAC$,
  }).pipe(
    map(({hasNAC, hasBAC}) => hasNAC || hasBAC),
  );

  viewModel$ = combineLatest({
    thermMode: this.controlModeService.thermMode$,
    coolingMode: this.controlModeService.coolingMode$,
    autoTempMode: this.controlModeService.autoTempMode$,
    temperatureControlMode: this.controlModeService.temperatureControlMode$,
    displayOff: this.displayOff$,
    hasThermostat: this.homesFacade.hasThermostat$,
  });

  TemperatureControlModes = TemperatureControlModes;
  ThermModes = ThermModes;
  CoolingModes = CoolingModes;
  AutoTempModes = AutoTempModes;

  constructor(
    private homesFacade: HomesFacade,
    private modulesFacade: ModulesFacade,
    private controlModeService: SidebarControlModeService,
  ) {}

  async toggleThermMode(currentMode: ThermModes, mode: ThermModes) {
    const homeId = await firstValueFrom(this.homesFacade.currentHomeId$);
    if (currentMode === mode) {
      this.controlModeService.selectMode$.next(ThermModes.SCHEDULE);
      await firstValueFrom(this.homesFacade.setHeatingMode(homeId, ThermModes.SCHEDULE));
    }
    else {
      this.controlModeService.selectMode$.next(mode);
      await firstValueFrom(this.homesFacade.setHeatingMode(homeId, mode));
    }
    this.homesFacade.loadHomeStatus({home_id: homeId});
  }

  async toggleCoolingMode(currentMode: CoolingModes, mode: CoolingModes) {
    const homeId = await firstValueFrom(this.homesFacade.currentHomeId$);
    if (currentMode === mode) {
      this.controlModeService.selectMode$.next(CoolingModes.SCHEDULE);
      await firstValueFrom(this.homesFacade.setCoolingMode(homeId, CoolingModes.SCHEDULE));
    }
    else {
      this.controlModeService.selectMode$.next(mode);
      await firstValueFrom(this.homesFacade.setCoolingMode(homeId, mode));
    }
    this.homesFacade.loadHomeStatus({home_id: homeId});
  }

  async toggleAutoTempMode (currentMode: AutoTempModes, mode: AutoTempModes) {
    const homeId = await firstValueFrom(this.homesFacade.currentHomeId$);
    if (currentMode === mode) {
      this.controlModeService.selectMode$.next(AutoTempModes.SCHEDULE);
      await firstValueFrom(this.homesFacade.setAutoMode(homeId, AutoTempModes.SCHEDULE));
    }
    else {
      this.controlModeService.selectMode$.next(mode);
      await firstValueFrom(this.homesFacade.setAutoMode(homeId, mode));
    }
    this.homesFacade.loadHomeStatus({home_id: homeId});
  }
}
